import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bot, Send, CheckCircle, XCircle, FileText, ExternalLink, Download, AlertTriangle, ThumbsUp, ThumbsDown, HelpCircle, MessageSquare, Trash2, Clock } from 'lucide-react';
import { useFormSubmission, useProcessSubmission, useDeleteSubmission } from '@/hooks/useForms';
import { useAiReview, useAiFollowUp } from '@/hooks/useAiAgent';
import { Button, Card, CardBody, CardHeader, Modal, Skeleton } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import { api } from '@/lib/api';
import type { AiReviewStatus, QualificationDecision, QualificationDetails, QualificationSignal, DynamicQuestion } from '@/types';

const statusConfig: Record<AiReviewStatus, { variant: 'default' | 'primary' | 'success' | 'warning' | 'error'; label: string }> = {
  PENDING: { variant: 'default', label: 'Pending Review' },
  REVIEWING: { variant: 'primary', label: 'AI Reviewing...' },
  COMPLETE: { variant: 'success', label: 'Review Complete' },
  FOLLOW_UP_SENT: { variant: 'warning', label: 'Follow-up Sent' },
  ERROR: { variant: 'error', label: 'Review Error' },
};

export default function FormSubmissionDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: submission, isLoading } = useFormSubmission(id);
  const navigate = useNavigate();
  const processSubmission = useProcessSubmission();
  const deleteMutation = useDeleteSubmission();
  const aiReview = useAiReview();
  const aiFollowUp = useAiFollowUp();
  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);

  const handleDelete = () => {
    if (window.confirm('Delete this submission? This cannot be undone.')) {
      deleteMutation.mutate(id!, {
        onSuccess: () => navigate('/admin/forms'),
      });
    }
  };

  if (isLoading) {
    return <Skeleton className="h-96 w-full rounded-xl" />;
  }

  if (!submission) {
    return (
      <Card padding="md">
        <p className="text-grey-500">Form submission not found.</p>
      </Card>
    );
  }

  const review = submission.aiReviewResult;
  const status = statusConfig[submission.aiReviewStatus];
  const formData = submission.formData || {};
  const contactName = submission.contact
    ? `${submission.contact.firstName} ${submission.contact.lastName}`
    : 'Unknown Contact';

  return (
    <div className="space-y-6">
      <Link
        to="/admin/forms"
        className="inline-flex items-center gap-1 text-sm text-grey-500 hover:text-secondary"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Submissions
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-secondary">{contactName}</h1>
            <Badge variant={submission.formType === 'RFP' ? 'primary' : 'default'}>
              {submission.formType}
            </Badge>
            <Badge variant={status.variant}>{status.label}</Badge>
          </div>
          <p className="text-body-sm text-grey-500 mt-1">
            Submitted {new Date(submission.createdAt).toLocaleString()}
            {submission.sourceUrl && (
              <> from <span className="text-accent">{submission.sourceUrl}</span></>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!submission.processed && (
            <Button
              onClick={() => processSubmission.mutate(id!)}
              loading={processSubmission.isPending}
            >
              <FileText className="w-4 h-4" />
              Create Deal
            </Button>
          )}
          {submission.processed && submission.deal && (
            <Link to={`/admin/deals/${submission.deal.id}`}>
              <Button variant="ghost">
                <ExternalLink className="w-4 h-4" />
                View Deal
              </Button>
            </Link>
          )}
          <Button
            variant="ghost"
            onClick={handleDelete}
            loading={deleteMutation.isPending}
            className="text-grey-400 hover:text-error"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Data */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader title="Form Data" />
            <CardBody className="space-y-3">
              {Object.entries(formData).map(([key, value]) => (
                <div key={key} className="flex gap-4">
                  <span className="text-sm text-grey-500 w-40 flex-shrink-0 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}
                  </span>
                  <span className="text-sm text-secondary">
                    {typeof value === 'object' ? JSON.stringify(value) : String(value || '—')}
                  </span>
                </div>
              ))}
            </CardBody>
          </Card>

          {/* AI SDR Assessment */}
          {submission.qualificationDecision && (
            <AiSdrAssessmentCard
              decision={submission.qualificationDecision}
              confidence={submission.qualificationConfidence}
              reasoning={submission.qualificationReasoning}
              details={submission.qualificationDetails}
              dynamicQuestions={submission.aiGeneratedQuestions}
              aiQuestionAnswers={submission.aiQuestionAnswers as Record<string, string> | undefined}
            />
          )}

          {/* AI Review Result */}
          {review && (
            <Card>
              <CardHeader
                title="AI Review"
                subtitle={review.summary}
              />

              <CardBody className="space-y-6">
                {/* Qualification Status — only show when a score has actually been computed */}
                {review.qualificationScore != null ? (
                <div className="flex items-start gap-4 p-4 rounded-lg border border-grey-200 bg-grey-50">
                  {review.qualificationScore >= 50 ? (
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
                      <ThumbsUp className="h-6 w-6 text-success" />
                    </div>
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
                      <ThumbsDown className="h-6 w-6 text-error" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span
                        className={`text-3xl font-bold ${
                          review.qualificationScore >= 70
                            ? 'text-success'
                            : review.qualificationScore >= 50
                              ? 'text-warning'
                              : 'text-error'
                        }`}
                      >
                        {review.qualificationScore}
                      </span>
                      <Badge
                        variant={review.qualificationScore >= 50 ? 'success' : 'error'}
                        size="sm"
                      >
                        {review.qualificationScore >= 50 ? 'Qualified' : 'Not Qualified'}
                      </Badge>
                    </div>
                    <p className="text-sm text-grey-600 mt-1">
                      {review.answeredQuestions?.length || 0} questions answered, {review.unansweredQuestions?.length || 0} unanswered
                    </p>
                    {review.qualificationScore < 50 && (
                      <p className="text-sm text-error mt-2">
                        <AlertTriangle className="w-4 h-4 inline mr-1" />
                        {review.unansweredQuestions?.some((q) => q.importance === 'REQUIRED')
                          ? 'Missing answers to required questions'
                          : 'Insufficient information provided to qualify this lead'}
                      </p>
                    )}
                  </div>
                </div>
                ) : (
                <div className="flex items-start gap-4 p-4 rounded-lg border border-grey-200 bg-grey-50">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-info/10">
                    <Clock className="h-6 w-6 text-info" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Badge variant="info" size="sm">Pending Qualification</Badge>
                    </div>
                    <p className="text-sm text-grey-600 mt-1">
                      {review.answeredQuestions?.length || 0} questions answered, {review.unansweredQuestions?.length || 0} unanswered
                    </p>
                    <p className="text-sm text-grey-500 mt-2">
                      Qualification score will be calculated after the prospect completes the intake wizard.
                    </p>
                  </div>
                </div>
                )}

                {/* Answered Questions */}
                {review.answeredQuestions.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-secondary mb-2">Answered Questions</h4>
                    <div className="space-y-2">
                      {review.answeredQuestions.map((q, i) => (
                        <div key={i} className="flex gap-3 p-3 rounded-lg bg-success/5">
                          <CheckCircle className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="text-sm text-secondary">{q.question}</div>
                            <div className="text-sm text-grey-600 mt-0.5">{q.answer}</div>
                            <Badge variant="default" size="sm" className="mt-1">
                              {q.importance}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Questions Not Addressed */}
                {review.unansweredQuestions.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-secondary mb-2">Not Addressed in Submission</h4>
                    <div className="space-y-2">
                      {review.unansweredQuestions.map((q, i) => (
                        <div key={i} className="flex gap-3 p-3 rounded-lg bg-grey-50">
                          <HelpCircle className="w-4 h-4 text-grey-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="text-sm text-secondary">{q.question}</div>
                            <Badge
                              variant="default"
                              size="sm"
                              className="mt-1"
                            >
                              {q.importance}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Follow-up Email Preview - Only show for unqualified with unanswered questions */}
                {review.followUpEmail && review.unansweredQuestions?.length > 0 && !submission.aiFollowUpSent && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-secondary">AI-Generated Follow-up Email</h4>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowFollowUpDialog(true)}
                      >
                        <Send className="w-3.5 h-3.5" />
                        Review & Send
                      </Button>
                    </div>
                    <p className="text-xs text-grey-500 mb-3">
                      This email asks for the missing information. Review the content before sending.
                    </p>
                    <div className="p-4 rounded-lg bg-grey-50 border border-grey-200">
                      <div className="text-sm font-medium text-secondary mb-2">
                        Subject: {review.followUpEmail.subject}
                      </div>
                      <div
                        className="text-sm text-grey-600 prose prose-sm max-w-none line-clamp-6"
                        dangerouslySetInnerHTML={{ __html: review.followUpEmail.body }}
                      />
                    </div>
                  </div>
                )}
                {submission.aiFollowUpSent && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-success/5 border border-success/20">
                    <CheckCircle className="w-4 h-4 text-success" />
                    <span className="text-sm text-success">Follow-up email has been sent</span>
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact Info */}
          <Card>
            <CardHeader title="Contact" />
            <CardBody className="space-y-3">
              {submission.contact ? (
                <>
                  <Link
                    to={`/admin/contacts/${submission.contact.id}`}
                    className="text-sm text-accent hover:underline"
                  >
                    {submission.contact.firstName} {submission.contact.lastName}
                  </Link>
                  {submission.contact.email && (
                    <p className="text-xs text-grey-500">{submission.contact.email}</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-grey-500">No linked contact</p>
              )}
            </CardBody>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader title="AI Actions" />
            <CardBody className="space-y-3">
              {submission.aiReviewStatus === 'PENDING' && (
                <Button
                  className="w-full"
                  onClick={() => aiReview.mutate(id!)}
                  loading={aiReview.isPending}
                >
                  <Bot className="w-4 h-4" />
                  Run AI Review
                </Button>
              )}
              {submission.aiReviewStatus === 'ERROR' && (
                <Button
                  className="w-full"
                  variant="ghost"
                  onClick={() => aiReview.mutate(id!)}
                  loading={aiReview.isPending}
                >
                  <Bot className="w-4 h-4" />
                  Retry Review
                </Button>
              )}
              {submission.aiReviewStatus === 'COMPLETE' && !submission.aiFollowUpSent && (review?.unansweredQuestions?.length ?? 0) > 0 && (
                <Button
                  className="w-full"
                  variant="ghost"
                  onClick={() => setShowFollowUpDialog(true)}
                >
                  <Send className="w-4 h-4" />
                  Review Follow-up Email
                </Button>
              )}
              {submission.aiReviewStatus === 'COMPLETE' && review?.unansweredQuestions?.length === 0 && (
                <p className="text-xs text-grey-500 text-center">All questions answered - no follow-up needed</p>
              )}
              {submission.aiFollowUpSent && (
                <p className="text-xs text-success text-center">Follow-up email sent</p>
              )}
            </CardBody>
          </Card>

          {/* Metadata */}
          <Card>
            <CardHeader title="Details" />
            <CardBody className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-grey-500">Visitor ID</span>
                <span className="text-secondary font-mono text-xs">
                  {submission.visitorId || '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-grey-500">Processed</span>
                <span className="text-secondary">
                  {submission.processed ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-grey-500">Follow-up</span>
                <span className="text-secondary">
                  {submission.aiFollowUpSent ? 'Sent' : 'Not sent'}
                </span>
              </div>
            </CardBody>
          </Card>

          {/* Documents */}
          {(submission.hasDocument || (submission.documents && submission.documents.length > 0)) && (
            <Card>
              <CardHeader title="Documents" />
              <CardBody className="space-y-2">
                {/* Primary document */}
                {submission.hasDocument && submission.documentName && (
                  <DocumentRow
                    submissionId={submission.id}
                    fileName={submission.documentName}
                    documentType="PRIMARY"
                    isPrimary
                  />
                )}
                {/* Additional documents */}
                {submission.documents?.map((doc) => (
                  <DocumentRow
                    key={doc.id}
                    submissionId={submission.id}
                    docId={doc.id}
                    fileName={doc.fileName}
                    documentType={doc.documentType}
                  />
                ))}
              </CardBody>
            </Card>
          )}
        </div>
      </div>

      {/* Follow-up Email Confirmation Dialog */}
      {showFollowUpDialog && review?.followUpEmail && (
        <Modal
          open={showFollowUpDialog}
          onOpenChange={setShowFollowUpDialog}
          title="Review Follow-up Email"
          description="This email will be sent to request missing information. Please review before sending."
          footer={
            <>
              <Button variant="ghost" onClick={() => setShowFollowUpDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  aiFollowUp.mutate(id!);
                  setShowFollowUpDialog(false);
                }}
                loading={aiFollowUp.isPending}
              >
                <Send className="w-4 h-4" />
                Send Email
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-grey-600">To</p>
              <p className="text-sm text-secondary">{submission.contact?.email || formData.email || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-grey-600">Subject</p>
              <p className="text-sm text-secondary">{review.followUpEmail.subject}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-grey-600">Message</p>
              <div className="mt-1 p-3 bg-grey-50 rounded-lg border border-grey-200 max-h-64 overflow-y-auto">
                <div
                  className="text-sm text-grey-700 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: review.followUpEmail.body }}
                />
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// AI SDR Assessment Card component
function AiSdrAssessmentCard({
  decision,
  confidence,
  reasoning,
  details,
  dynamicQuestions,
  aiQuestionAnswers,
}: {
  decision: QualificationDecision;
  confidence?: number;
  reasoning?: string;
  details?: QualificationDetails;
  dynamicQuestions?: DynamicQuestion[];
  aiQuestionAnswers?: Record<string, string>;
}) {
  const decisionConfig: Record<QualificationDecision, { variant: 'success' | 'warning' | 'error'; label: string; icon: typeof CheckCircle; color: string }> = {
    QUALIFIED: { variant: 'success', label: 'Qualified', icon: ThumbsUp, color: 'text-success' },
    UNCERTAIN: { variant: 'warning', label: 'Uncertain', icon: HelpCircle, color: 'text-warning' },
    NOT_QUALIFIED: { variant: 'error', label: 'Not Qualified', icon: ThumbsDown, color: 'text-error' },
  };

  const config = decisionConfig[decision];
  const DecisionIcon = config.icon;
  const confidencePercent = confidence ? Math.round(Number(confidence)) : 0;

  return (
    <Card>
      <CardHeader
        title="AI SDR Assessment"
        subtitle="Automated qualification based on ICP and enrichment data"
      />

      <CardBody className="space-y-6">
        {/* Decision Summary */}
        <div className="flex items-start gap-4 p-4 rounded-lg border border-grey-200 bg-grey-50">
          <div className={`flex h-12 w-12 items-center justify-center rounded-full ${
            decision === 'QUALIFIED' ? 'bg-success/10' :
            decision === 'UNCERTAIN' ? 'bg-warning/10' : 'bg-error/10'
          }`}>
            <DecisionIcon className={`h-6 w-6 ${config.color}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <Badge variant={config.variant} size="md">
                {config.label}
              </Badge>
              {confidence !== undefined && (
                <span className="text-sm text-grey-500">
                  {confidencePercent}% confidence
                </span>
              )}
            </div>
            {reasoning && (
              <p className="text-sm text-grey-600 mt-2">{reasoning}</p>
            )}
          </div>
        </div>

        {/* Qualification Details */}
        {details && (
          <div>
            <h4 className="text-sm font-medium text-secondary mb-3">Qualification Breakdown</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <FitIndicator
                label="ICP Score"
                value={`${details.icpScore ?? 0}/100`}
                status={details.icpScore >= 70 ? 'good' : details.icpScore >= 50 ? 'unknown' : 'poor'}
              />
              <FitIndicator
                label="Budget Fit"
                value={details.budgetFit.replace('_', ' ')}
                status={details.budgetFit}
              />
              <FitIndicator
                label="Company Fit"
                value={details.companyFit}
                status={details.companyFit}
              />
              <FitIndicator
                label="Authority"
                value={details.authorityLevel.replace('_', ' ')}
                status={details.authorityLevel}
              />
            </div>
          </div>
        )}

        {/* Qualification Signals */}
        {details?.signals && details.signals.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-secondary mb-3">Signals</h4>
            <div className="space-y-2">
              {details.signals.map((signal: QualificationSignal, i: number) => (
                <div
                  key={i}
                  className={`flex items-start gap-2 p-2 rounded-lg ${
                    signal.type === 'green' ? 'bg-success/5' :
                    signal.type === 'yellow' ? 'bg-warning/5' : 'bg-error/5'
                  }`}
                >
                  {signal.type === 'green' ? (
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  ) : signal.type === 'yellow' ? (
                    <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                  )}
                  <span className="text-sm text-grey-700">{signal.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI-Generated Questions & Answers */}
        {dynamicQuestions && dynamicQuestions.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-secondary mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-grey-400" />
              AI-Generated Questions
            </h4>
            <div className="space-y-2">
              {dynamicQuestions.map((q: DynamicQuestion, i: number) => {
                const answer = aiQuestionAnswers?.[q.question];
                return (
                  <div key={i} className="p-3 rounded-lg bg-grey-50 border border-grey-200">
                    <p className="text-sm font-medium text-secondary">{q.question}</p>
                    <p className="text-xs text-grey-400 mt-0.5">{q.reason}</p>
                    {answer && (
                      <div className="mt-2 p-2 rounded bg-white border border-grey-100">
                        <p className="text-sm text-grey-700">{answer}</p>
                      </div>
                    )}
                    {!answer && (
                      <Badge variant="default" size="sm" className="mt-2">
                        Unanswered
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// Fit indicator component
function FitIndicator({
  label,
  value,
  status,
}: {
  label: string;
  value: string;
  status: string;
}) {
  const statusColors: Record<string, string> = {
    good: 'bg-success/10 text-success border-success/20',
    low: 'bg-error/10 text-error border-error/20',
    poor: 'bg-error/10 text-error border-error/20',
    red_flag: 'bg-error/10 text-error border-error/20',
    too_far: 'bg-warning/10 text-warning border-warning/20',
    unknown: 'bg-grey-100 text-grey-500 border-grey-200',
    decision_maker: 'bg-success/10 text-success border-success/20',
    influencer: 'bg-warning/10 text-warning border-warning/20',
  };

  const colorClass = statusColors[status] || statusColors.unknown;

  return (
    <div className={`p-3 rounded-lg border ${colorClass}`}>
      <p className="text-xs font-medium opacity-80">{label}</p>
      <p className="text-sm font-semibold capitalize mt-0.5">{value}</p>
    </div>
  );
}

// Document row component with download functionality
function DocumentRow({
  submissionId,
  docId,
  fileName,
  documentType,
  isPrimary,
}: {
  submissionId: string;
  docId?: string;
  fileName: string;
  documentType: string;
  isPrimary?: boolean;
}) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const endpoint = isPrimary
        ? `/forms/submissions/${submissionId}/download`
        : `/forms/submissions/${submissionId}/documents/${docId}/download`;
      const response = await api.get<{ url: string; fileName: string }>(endpoint);
      // Open the presigned URL in a new tab to download
      window.open(response.data.url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center justify-between p-2 rounded-lg bg-grey-50 hover:bg-grey-100 transition-colors">
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="w-4 h-4 text-grey-400 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm text-secondary truncate">{fileName}</p>
          <p className="text-xs text-grey-400">
            {isPrimary ? 'Primary RFP' : documentType}
          </p>
        </div>
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={handleDownload}
        loading={downloading}
        aria-label="Download"
      >
        <Download className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}
